import { Injectable, computed, inject, Injector } from '@angular/core';
import { DataService } from './data.service';
import { AuthService } from '../auth/auth.service';
import { ActingContextService } from './acting-context.service';
import { collection, doc, writeBatch, query, where, getDocs, updateDoc, deleteDoc, getDoc, addDoc, setDoc, WriteBatch } from 'firebase/firestore'; // FIX: Consolidated imports
import { FIRESTORE, FUNCTIONS } from '../firebase.config';
import { NewCommitmentPayload, ClientCommitment, LoanStatus, ClientLead, AccountType, FullLoanDeal, LoanLedgerEntryType, HeadsOfTermsDbData, LoanLedgerEntry } from '../models/loan.model';
import { UserProfile } from '../models/user.model';
import { TicketService } from './ticket.service';
import { NotificationService } from './notification.service';
import { LOAN_LEDGER_ENTRY_TYPE, LOAN_STATUS, TICKET_STATUS } from '../constants';
import { httpsCallable } from 'firebase/functions';

export interface ClientDetails extends UserProfile {
  commitments: (ClientCommitment & { dealName: string; dealId: string; dealStatus: LoanStatus })[];
  totalCommitted: number;
  activeLoanDealsCount: number;
  fundedLoanDealsCount: number;
  finishedLoanDealsCount: number;
  hasOpenTickets: boolean;
}

export interface DeleteClientAccountResult {
  success: boolean;
  uid: string;
  deleted: {
    commitments: number;
    loanLedgerEntries: number;
    tickets: number;
    notifications: number;
    redemptionStatements: number;
    relationships: number;
    approvalRequests: number;
    activityLogs: number;
    corporateKycTokens: number;
    kycSubmissions: number;
    corporateKycSubmissions: number;
    clientLeads: number;
    storageFiles: number;
    authDeleted: boolean;
  };
}

@Injectable({
  providedIn: 'root'
})
export class ClientService {
  private dataService: DataService = inject(DataService);
  // FIX: Explicitly type injected service to resolve 'unknown' type error.
  private authService: AuthService = inject(AuthService);
  private injector = inject(Injector);
  private ticketService: TicketService = inject(TicketService);
  private notificationService = inject(NotificationService);
  private adminNotificationService: any; // Lazy loaded to avoid circular dependency
  private readonly deleteClientAccountCallable = httpsCallable<{ uid: string }, DeleteClientAccountResult>(
    FUNCTIONS,
    'deleteClientAccount'
  );

  constructor() { }

  // Raw Data Signals
  allClients = computed(() => {
    const clients = this.dataService.users().filter(u => u.role?.toLowerCase() === 'client');
    console.log('ClientService: allClients count:', clients.length, 'total users:', this.dataService.users().length);
    if (clients.length > 0) {
      console.log('ClientService: first client sample:', clients[0].displayName, 'role:', clients[0].role);
    }
    return clients;
  });
  allCommitments = this.dataService.clientCommitments;
  allNotifications = this.dataService.notifications;
  allClientLeads = this.dataService.clientLeads;

  // Computed Signals for UI


  clientsWithDetails = computed<ClientDetails[]>(() => {
    const clients = this.allClients();
    const commitments = this.allCommitments();
    const deals = this.dataService.allDeals();
    const tickets = this.ticketService.allTicketsSorted();

    const dealsMap = new Map<string, FullLoanDeal>(deals.map(d => [d.loanDeal.id, d]));

    return clients.map(client => {
      const clientCommitments = commitments
        .filter(c => c.userId === client.uid)
        .map(c => {
          const deal = dealsMap.get(c.loanDealId);
          return {
            ...c,
            dealName: deal?.loanDeal.loanDealName ?? 'Unknown Deal',
            dealId: deal?.loanDeal.id ?? '',
            dealStatus: deal?.loanDeal.status ?? LOAN_STATUS.DRAFT,
          };
        });

      const totalCommitted = clientCommitments.reduce((sum, c) => sum + c.committedAmount, 0);

      const dealIds = new Set<string>(clientCommitments.map(c => c.loanDealId));
      let activeLoanDealsCount = 0;
      let fundedLoanDealsCount = 0;
      let finishedLoanDealsCount = 0;

      for (const id of dealIds) {
        const deal = dealsMap.get(id);
        if (deal) {
          if (deal.loanDeal.status === LOAN_STATUS.ACTIVE) activeLoanDealsCount++;
          if (deal.loanDeal.status === LOAN_STATUS.FUNDED || deal.loanDeal.status === LOAN_STATUS.EXTENDED) fundedLoanDealsCount++;
          if (deal.loanDeal.status === LOAN_STATUS.FINISHED) finishedLoanDealsCount++;
        }
      }

      const hasOpenTickets = tickets.some(t => t.userId === client.uid && t.status === TICKET_STATUS.OPEN);

      return {
        ...client,
        commitments: clientCommitments,
        totalCommitted,
        activeLoanDealsCount,
        fundedLoanDealsCount,
        finishedLoanDealsCount,
        hasOpenTickets,
      };
    });
  });

  pendingCommitmentsValue = computed(() => {
    return this.allCommitments()
      .filter(c => c.isConfirmed && !c.fundsReceived)
      .reduce((sum, c) => sum + c.committedAmount, 0);
  });

  recentCommitments = computed(() => {
    return [...this.allCommitments()]
      .sort((a, b) => new Date(b.commitmentDate).getTime() - new Date(a.commitmentDate).getTime())
      .slice(0, 5);
  });

  // Client-facing data - respects acting context for trustees/read-only users
  commitmentsForCurrentUser = computed(() => {
    // Use Injector to get ActingContextService lazily to avoid circular dependency
    // and ensure we're tracking the signal
    const actingContext = this.injector.get(ActingContextService);
    const effectiveUserId = actingContext.effectiveUserId() || this.authService.userProfile()?.uid;

    if (!effectiveUserId) return [];
    return this.allCommitments().filter(c => c.userId === effectiveUserId);
  });

  // Filtered notifications for the current acting user
  notificationsForCurrentUser = computed(() => {
    const actingContext = this.injector.get(ActingContextService);
    const effectiveUserId = actingContext.effectiveUserId() || this.authService.userProfile()?.uid;

    if (!effectiveUserId) return [];
    return this.allNotifications().filter(n => n.userId === effectiveUserId);
  });

  // Computed Signals for UI - Updated to use filtered notifications
  unreadNotifications = computed(() => this.notificationsForCurrentUser().filter(n => !n.read));

  /**
   * Check if a user has any active trustee/read-only relationships
   * This is async because it needs to query Firestore
   */
  async hasActiveRelationships(userId: string): Promise<boolean> {
    console.log('[TRUSTEE DEBUG] hasActiveRelationships() called for userId:', userId);
    try {
      console.log('[TRUSTEE DEBUG] Querying clientRelationships collection...');
      // FIXED: Query by primaryClientId (trustee) instead of associatedUserId to match UI convention
      const relationships = await this.dataService.query<import('../models/relationship.model').ClientRelationship>('clientRelationships', [
        { field: 'primaryClientId', operator: '==', value: userId },
        { field: 'isActive', operator: '==', value: true }
      ]);
      console.log('[TRUSTEE DEBUG] Query completed. Found relationships:', relationships.length);
      if (relationships.length > 0) {
        console.log('[TRUSTEE DEBUG] Relationships:', relationships);
      }
      return relationships.length > 0;
    } catch (error) {
      console.error('[TRUSTEE DEBUG] Error checking relationships:', error);
      console.error('[TRUSTEE DEBUG] Error details:', {
        message: error instanceof Error ? error.message : 'Unknown error',
        code: (error as any)?.code,
        details: (error as any)?.details
      });
      return false;
    }
  }

  /**
   * REQUEST approval to create a client relationship (four-eyes governance)
   */
  async requestCreateClientRelationship(
    primaryClientId: string,
    primaryClientName: string,
    associatedUserId: string,
    associatedUserName: string,
    relationshipType: 'Trustee' | 'ReadOnly',
    canCommit: boolean = false
  ): Promise<string> {
    const admin = this.authService.userProfile();
    if (!admin || admin.role !== 'Admin') {
      throw new Error('Only admins can request client relationships');
    }

    // Import ApprovalService dynamically to avoid circular dependency
    const { ApprovalService } = await import('./approval.service');
    const approvalService = this.injector.get(ApprovalService);

    return await approvalService.createApprovalRequest({
      type: 'ASSOCIATED_USER_CREATE',
      description: `Add ${relationshipType} access: ${associatedUserName} → ${primaryClientName}`,
      trusteeId: associatedUserId,
      trusteeName: associatedUserName,
      clientId: primaryClientId,
      clientName: primaryClientName,
      relationshipType,
      canCommit,
      metadata: {
        relationshipType,
        canCommit
      }
    });
  }

  /**
   * REQUEST approval to add a client account (four-eyes governance)
   */
  async requestAddClientAccount(
    userId: string,
    userName: string,
    accountData: import('../models/account.model').ClientAccount
  ): Promise<string> {
    const admin = this.authService.userProfile();
    if (!admin || admin.role !== 'Admin') {
      throw new Error('Only admins can request client account additions');
    }

    // Import ApprovalService dynamically to avoid circular dependency
    const { ApprovalService } = await import('./approval.service');
    const approvalService = this.injector.get(ApprovalService);

    return await approvalService.createApprovalRequest({
      type: 'CLIENT_ACCOUNT_CREATE',
      description: `Add ${accountData.accountType} account for ${userName}`,
      userId,
      userName,
      accountType: accountData.accountType,
      accountNumber: accountData.id, // Account ID serves as reference
      metadata: {
        accountData
      }
    });
  }

  // Methods
  async addCommitments(
    dealId: string,
    dealName: string,
    newCommitments: (NewCommitmentPayload & { trancheId?: string })[],
    isAdmin = false,
    options?: { activateOnValueDate?: boolean }
  ): Promise<string[]> {
    console.log('[COMMIT DEBUG] addCommitments called with:', {
      dealId,
      dealName,
      commitmentCount: newCommitments.length,
      commitments: newCommitments,
      isAdmin
    });

    const batch = writeBatch(FIRESTORE);
    const commitmentsCollection = collection(FIRESTORE, 'clientCommitments');
    const now = new Date().toISOString();
    const createdIds: string[] = [];

    for (const commitment of newCommitments) {
      console.log('[COMMIT DEBUG] Processing commitment for userId:', commitment.userId);

      try {
        const userDoc = await getDoc(doc(FIRESTORE, 'users', commitment.userId));
        console.log('[COMMIT DEBUG] User doc exists:', userDoc.exists());

        if (!userDoc.exists()) {
          console.error(`[COMMIT DEBUG] User profile not found for userId: ${commitment.userId}`);
          continue;
        }

        const userProfile = userDoc.data() as UserProfile;
        console.log('[COMMIT DEBUG] User profile loaded:', {
          uid: userProfile.uid,
          displayName: userProfile.displayName,
          accountCount: userProfile.accounts?.length || 0
        });

        const clientAccount = userProfile.accounts?.find(acc => acc.id === commitment.clientAccountId);

        if (!clientAccount) {
          console.error(`[COMMIT DEBUG] Client account not found for accountId: ${commitment.clientAccountId}`, {
            availableAccounts: userProfile.accounts?.map(a => a.id)
          });
          continue;
        }

        console.log('[COMMIT DEBUG] Client account found:', {
          accountId: clientAccount.id,
          accountType: clientAccount.accountType,
          bankName: clientAccount.bankName
        });

        const commitmentRef = doc(commitmentsCollection);
        createdIds.push(commitmentRef.id);
        const effectiveValueDate = commitment.valueDate ? this.normalizeCommitmentValueDate(commitment.valueDate) : null;
        const shouldActivateOnValueDate = Boolean(options?.activateOnValueDate && effectiveValueDate);
        const operatorId = this.authService.userProfile()?.uid || 'system';
        const newCommitmentData: ClientCommitment = {
          id: commitmentRef.id,
          loanDealId: dealId,
          userId: commitment.userId,
          clientName: commitment.clientName,
          accountType: clientAccount.accountType,
          clientAccountId: commitment.clientAccountId,
          committedAmount: commitment.committedAmount,
          currentPrincipal: commitment.committedAmount,
          totalPrincipalRedeemed: 0,
          commitmentDate: now,
          isConfirmed: shouldActivateOnValueDate,
          fundsReceived: shouldActivateOnValueDate,
          ...(shouldActivateOnValueDate ? {
            dateConfirmed: now,
            confirmedByUserId: operatorId,
            dateFundsReceived: effectiveValueDate as string,
          } : {}),
          ...(commitment.trancheId && { trancheId: commitment.trancheId }),
        };

        console.log('[COMMIT DEBUG] Created commitment data:', {
          id: newCommitmentData.id,
          userId: newCommitmentData.userId,
          clientName: newCommitmentData.clientName,
          amount: newCommitmentData.committedAmount
        });

        // Dual-write: top-level and nested
        console.log('[COMMIT DEBUG] Writing to paths:', {
          topLevel: `clientCommitments/${commitmentRef.id}`,
          userSubcollection: `users/${commitment.userId}/clientCommitments/${commitmentRef.id}`
        });

        batch.set(commitmentRef, newCommitmentData);
        const userCommitmentRef = doc(FIRESTORE, `users/${commitment.userId}/clientCommitments/${commitmentRef.id}`);
        batch.set(userCommitmentRef, newCommitmentData);

        if (shouldActivateOnValueDate && effectiveValueDate) {
          const ledgerRef = doc(collection(FIRESTORE, 'loanLedger'));
          const ledgerEntry: Omit<LoanLedgerEntry, 'id'> = {
            loanDealId: dealId,
            date: now,
            valueDate: effectiveValueDate,
            description: `Client Funds Received - ${commitment.clientName} (${clientAccount.accountType})`,
            amount: commitment.committedAmount,
            entryType: LOAN_LEDGER_ENTRY_TYPE.CLIENT_FUNDS_IN,
            sourceId: commitmentRef.id,
            accountType: clientAccount.accountType,
            createdBy: operatorId,
          };
          batch.set(ledgerRef, ledgerEntry);
        }
      } catch (error) {
        console.error('[COMMIT DEBUG] Error processing commitment:', error);
        throw error;
      }
    }

    // Create a notification for the client using NotificationService
    if (!isAdmin && newCommitments.length > 0) {
      const totalAmount = newCommitments.reduce((sum, c) => sum + c.committedAmount, 0);
      console.log('[COMMIT DEBUG] Creating notification for userId:', newCommitments[0].userId);
      await this.notificationService.notifyCommitmentReceived(
        newCommitments[0].userId,
        dealId,
        dealName,
        totalAmount,
        batch
      );
    }

    console.log('[COMMIT DEBUG] Committing batch...');
    try {
      await batch.commit();
      console.log('[COMMIT DEBUG] Batch committed successfully!');
    } catch (error) {
      console.error('[COMMIT DEBUG] Error committing batch:', error);
      throw error;
    }
    return createdIds;
  }

  private normalizeCommitmentValueDate(valueDate: string): string {
    if (/^\d{4}-\d{2}-\d{2}$/.test(valueDate)) {
      const [year, month, day] = valueDate.split('-').map(Number);
      return new Date(Date.UTC(year, month - 1, day, 12, 0, 0, 0)).toISOString();
    }

    const parsed = new Date(valueDate);
    if (Number.isNaN(parsed.getTime())) {
      throw new Error(`Invalid commitment value date: ${valueDate}`);
    }

    return parsed.toISOString();
  }

  async updateCommitmentFlags(
    commitmentId: string,
    userId: string,
    updates: {
      isConfirmed?: boolean;
      fundsReceived?: boolean;
      headsOfTermsSigned?: boolean;
      headsOfTermsPdfUrl?: string;
    },
    confirmedByUserId?: string,
    fundsReceivedDate?: string // Optional: custom date for funds received
  ): Promise<void> {
    const batch = writeBatch(FIRESTORE);

    const topLevelRef = doc(FIRESTORE, 'clientCommitments', commitmentId);
    const nestedRef = doc(FIRESTORE, `users/${userId}/clientCommitments/${commitmentId}`);

    const updatePayload: any = { ...updates };

    if (updates.isConfirmed) {
      updatePayload.dateConfirmed = new Date().toISOString();
      if (confirmedByUserId) {
        updatePayload.confirmedByUserId = confirmedByUserId;
      }
    }
    if (updates.fundsReceived) {
      // Use the provided date if available, otherwise default to now
      updatePayload.dateFundsReceived = fundsReceivedDate || new Date().toISOString();
    }
    if (updates.headsOfTermsSigned) {
      updatePayload.headsOfTermsSignedDate = new Date().toISOString();
    }
    // Also ensure if funds are received, it is also confirmed
    if (updates.fundsReceived === true) {
      updatePayload.isConfirmed = true;
      if (!updatePayload.dateConfirmed) {
        updatePayload.dateConfirmed = new Date().toISOString();
      }
    }

    // If un-confirming, also un-receive funds
    if (updates.isConfirmed === false) {
      updatePayload.fundsReceived = false;
    }

    // Dual-write the updates
    batch.update(topLevelRef, updatePayload);
    batch.update(nestedRef, updatePayload);

    await batch.commit();

    // Send notifications for commitment lifecycle events
    const commitmentDoc = await getDoc(topLevelRef);
    if (commitmentDoc.exists()) {
      const commitment = { id: commitmentId, ...commitmentDoc.data() } as ClientCommitment;
      const dealRef = doc(FIRESTORE, 'loanDeals', commitment.loanDealId);
      const dealDoc = await getDoc(dealRef);
      const dealName = dealDoc.exists() ? (dealDoc.data() as any).loanDealName : 'Unknown Deal';

      // Notify on confirmation (only when newly confirmed, not when unconfirming)
      if (updates.isConfirmed === true && !commitment.isConfirmed) {
        await this.notificationService.notifyCommitmentConfirmed(commitment, dealName);
      }

      // Notify on funds received (only when newly marked as received)
      if (updates.fundsReceived === true && !commitment.fundsReceived) {
        await this.notificationService.notifyFundsReceived(
          commitment,
          dealName,
          commitment.committedAmount
        );

        // Also notify admins for visual confirmation
        try {
          const { AdminNotificationService } = await import('./admin-notification.service');
          if (!this.adminNotificationService) {
            this.adminNotificationService = this.injector.get(AdminNotificationService);
          }

          await this.adminNotificationService.notifyAdminsAboutPendingAction({
            id: `funds-received-${commitmentId}`,
            type: 'FUNDS',
            title: 'Funds Confirmation Needed',
            description: `${commitment.clientName} - ${new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(commitment.committedAmount)}`,
            priority: 'HIGH',
            link: ['/admin/clients', commitment.userId],
            timestamp: new Date().toISOString(),
            metadata: { commitmentId, userId: commitment.userId }
          });
        } catch (error) {
          console.error('Failed to send admin notification for funds:', error);
        }
      }

      // Notify on HoT signed (only when newly signed)
      if (updates.headsOfTermsSigned === true && !commitment.headsOfTermsSigned) {
        await this.notificationService.notifyHoTSigned(commitment, dealName);
      }
    }
  }

  async manuallyConfirmFundsReceived(commitmentId: string, userId: string): Promise<void> {
    await this.updateCommitmentFlags(commitmentId, userId, { fundsReceived: true });
  }

  async removeCommitment(commitmentId: string, userId: string): Promise<void> {
    const batch = writeBatch(FIRESTORE);
    const userRole = this.authService.currentUserRole();

    // Define references for both the top-level and nested documents
    const topLevelRef = doc(FIRESTORE, 'clientCommitments', commitmentId);
    const nestedRef = doc(FIRESTORE, `users/${userId}/clientCommitments/${commitmentId}`);

    // Both Admins and Clients must delete the nested document.
    batch.delete(nestedRef);

    // Only Admin users can perform operations on the top-level collection and the ledger,
    // as clients lack the necessary permissions.
    if (userRole === 'Admin') {
      batch.delete(topLevelRef);

      // Check for and remove associated ledger entry for a 'funds received' commitment.
      const ledgerQuery = query(
        collection(FIRESTORE, 'loanLedger'),
        where('sourceId', '==', commitmentId),
        where('entryType', '==', LOAN_LEDGER_ENTRY_TYPE.CLIENT_FUNDS_IN)
      );
      const existingLedgerSnapshot = await getDocs(ledgerQuery);
      if (!existingLedgerSnapshot.empty) {
        existingLedgerSnapshot.forEach(doc => batch.delete(doc.ref));
      }
    } else {
      // For non-admins (Clients/Trustees), we cannot delete the top-level document due to permissions.
      // We must remove it from the local cache manually so the UI updates immediately.
      this.dataService.removeLocalCommitment(commitmentId);
    }
    // A client deleting will leave an orphaned unconfirmed document in the top-level collection.
    // This is an acceptable trade-off to fix the permission error, as other clients only query for
    // confirmed commitments from this collection, so they will not see the orphaned data.

    await batch.commit();
  }

  async updateCommitmentAmount(commitmentId: string, newAmount: number, userId?: string): Promise<void> {
    console.log('[UPDATE DEBUG] updateCommitmentAmount called:', { commitmentId, newAmount, userId });

    const topLevelRef = doc(FIRESTORE, 'clientCommitments', commitmentId);

    try {
      let targetUserId = userId;

      // If userId is not provided, we must fetch it from the top-level document.
      // Note: This read might fail if the user (Client/Trustee) doesn't have read access to top-level.
      if (!targetUserId) {
        const commitmentDoc = await getDoc(topLevelRef);
        console.log('[UPDATE DEBUG] Commitment doc exists:', commitmentDoc.exists());

        if (!commitmentDoc.exists()) {
          throw new Error(`Commitment with id ${commitmentId} not found.`);
        }
        targetUserId = (commitmentDoc.data() as ClientCommitment).userId;
      }

      console.log('[UPDATE DEBUG] Commitment userId:', targetUserId);

      const batch = writeBatch(FIRESTORE);
      const nestedRef = doc(FIRESTORE, `users/${targetUserId}/clientCommitments/${commitmentId}`);
      const updatePayload = { committedAmount: newAmount };

      console.log('[UPDATE DEBUG] Updating paths:', {
        topLevel: `clientCommitments/${commitmentId}`,
        nested: `users/${targetUserId}/clientCommitments/${commitmentId}`
      });

      // Keep the shared top-level record in sync so admin views reflect client edits.
      batch.update(topLevelRef, updatePayload);
      batch.update(nestedRef, updatePayload);
      this.dataService.updateLocalCommitment(commitmentId, updatePayload);

      console.log('[UPDATE DEBUG] Committing batch...');
      await batch.commit();
      console.log('[UPDATE DEBUG] Batch committed successfully!');
    } catch (error) {
      console.error('[UPDATE DEBUG] Error updating commitment:', error);
      throw error;
    }
  }

  async reallocateCommitment(
    originalCommitmentId: string,
    newAllocations: { clientAccountId: string; amount: number }[],
    confirmedByUserId: string
  ): Promise<void> {
    const topLevelRef = doc(FIRESTORE, 'clientCommitments', originalCommitmentId);
    const commitmentDoc = await getDoc(topLevelRef);

    if (!commitmentDoc.exists()) {
      throw new Error(`Commitment with id ${originalCommitmentId} not found.`);
    }

    const originalCommitment = commitmentDoc.data() as ClientCommitment;
    const userId = originalCommitment.userId;
    const userDoc = await getDoc(doc(FIRESTORE, 'users', userId));
    const userProfile = userDoc.data() as UserProfile;

    if (!userProfile || !userProfile.accounts) {
      throw new Error('User profile or accounts not found.');
    }

    const batch = writeBatch(FIRESTORE);
    const now = new Date().toISOString();

    // 1. Handle the first allocation (update original commitment)
    if (newAllocations.length > 0) {
      const firstAllocation = newAllocations[0];
      const account = userProfile.accounts.find(a => a.id === firstAllocation.clientAccountId);

      if (!account) throw new Error(`Account ${firstAllocation.clientAccountId} not found`);

      const updatePayload = {
        committedAmount: firstAllocation.amount,
        currentPrincipal: firstAllocation.amount, // Reset principal to match new amount
        clientAccountId: firstAllocation.clientAccountId,
        accountType: account.accountType,
        isConfirmed: true,
        dateConfirmed: now,
        confirmedByUserId: confirmedByUserId
      };

      const nestedRef = doc(FIRESTORE, `users/${userId}/clientCommitments/${originalCommitmentId}`);
      batch.update(topLevelRef, updatePayload);
      batch.update(nestedRef, updatePayload);
    }

    // 2. Handle subsequent allocations (create new commitments)
    for (let i = 1; i < newAllocations.length; i++) {
      const allocation = newAllocations[i];
      const account = userProfile.accounts.find(a => a.id === allocation.clientAccountId);

      if (!account) throw new Error(`Account ${allocation.clientAccountId} not found`);

      const newCommitmentRef = doc(collection(FIRESTORE, 'clientCommitments'));
      const newCommitment: ClientCommitment = {
        ...originalCommitment,
        id: newCommitmentRef.id,
        committedAmount: allocation.amount,
        currentPrincipal: allocation.amount,
        clientAccountId: allocation.clientAccountId,
        accountType: account.accountType,
        isConfirmed: true,
        dateConfirmed: now,
        confirmedByUserId: confirmedByUserId,
        fundsReceived: false,
        headsOfTermsSigned: false, // Reset these for new split commitments
        dateFundsReceived: undefined,
      };

      const newNestedRef = doc(FIRESTORE, `users/${userId}/clientCommitments/${newCommitmentRef.id}`);
      batch.set(newCommitmentRef, newCommitment);
      batch.set(newNestedRef, newCommitment);
    }

    await batch.commit();

    // Notify about confirmation (we can just notify for the original one as a proxy for the action)
    const dealRef = doc(FIRESTORE, 'loanDeals', originalCommitment.loanDealId);
    const dealDoc = await getDoc(dealRef);
    const dealName = dealDoc.exists() ? (dealDoc.data() as any).loanDealName : 'Unknown Deal';

    await this.notificationService.notifyCommitmentConfirmed(originalCommitment, dealName);
  }

  async saveHeadsOfTermsData(commitmentId: string, data: HeadsOfTermsDbData): Promise<void> {
    const topLevelRef = doc(FIRESTORE, 'clientCommitments', commitmentId);

    const commitmentDoc = await getDoc(topLevelRef);
    if (!commitmentDoc.exists()) {
      throw new Error(`Commitment with id ${commitmentId} not found.`);
    }
    const userId = (commitmentDoc.data() as ClientCommitment).userId;

    const batch = writeBatch(FIRESTORE);
    const nestedRef = doc(FIRESTORE, `users/${userId}/clientCommitments/${commitmentId}`);
    const updatePayload = {
      headsOfTermsData: data
    };

    batch.update(topLevelRef, updatePayload);
    batch.update(nestedRef, updatePayload);

    await batch.commit();

    // Send notification about HoT data being saved (assuming this means it's being signed)
    const commitment = { id: commitmentId, ...commitmentDoc.data() } as ClientCommitment;
    const dealRef = doc(FIRESTORE, 'loanDeals', commitment.loanDealId);
    const dealDoc = await getDoc(dealRef);
    const dealName = dealDoc.exists() ? (dealDoc.data() as any).loanDealName : 'Unknown Deal';

    await this.notificationService.notifyHoTSigned(commitment, dealName);
  }

  async markNotificationAsRead(notificationId: string): Promise<void> {
    const topLevelRef = doc(FIRESTORE, 'notifications', notificationId);

    const notificationDoc = await getDoc(topLevelRef);
    if (!notificationDoc.exists()) {
      // It might only exist in the user's subcollection if it failed previously
      // This is a recovery mechanism, but ideally shouldn't be hit often.
      const user = this.authService.userProfile();
      if (user) {
        const nestedRef = doc(FIRESTORE, `users/${user.uid}/notifications/${notificationId}`);
        await updateDoc(nestedRef, { read: true });
      }
      return;
    }
    const userId = notificationDoc.data().userId;

    const batch = writeBatch(FIRESTORE);
    const nestedRef = doc(FIRESTORE, `users/${userId}/notifications/${notificationId}`);

    batch.update(topLevelRef, { read: true });
    batch.update(nestedRef, { read: true });

    await batch.commit();
  }

  async markAllNotificationsAsRead(): Promise<void> {
    const user = this.authService.userProfile();
    if (!user) return;

    const unread = this.unreadNotifications().filter(n => n.userId === user.uid);
    if (unread.length === 0) return;

    const batch = writeBatch(FIRESTORE);
    unread.forEach(notification => {
      const topLevelRef = doc(FIRESTORE, 'notifications', notification.id);
      const nestedRef = doc(FIRESTORE, `users/${user.uid}/notifications/${notification.id}`);
      batch.update(topLevelRef, { read: true });
    });
    await batch.commit();
  }

  async updateUserProfile(userId: string, updates: Partial<UserProfile>): Promise<void> {
    return this.dataService.updateDocument(`users/${userId}`, updates);
  }

  async deleteClientAccount(userId: string): Promise<DeleteClientAccountResult> {
    try {
      const result = await this.deleteClientAccountCallable({ uid: userId });
      return result.data;
    } catch (error) {
      if (!this.shouldFallbackToFirestoreDeletion(error)) {
        throw error;
      }

      console.warn('deleteClientAccount callable failed, falling back to Firestore cleanup.', error);
      return await this.deleteClientAccountViaFirestore(userId);
    }
  }

  async updateAccountantEmail(userId: string, email: string): Promise<void> {
    const updates: Partial<UserProfile> = {
      accountantEmail: email,
      accountantEmailAddedAt: new Date().toISOString()
    };
    await this.updateUserProfile(userId, updates);

    // Auto-refresh: Update the local user profile state
    const currentUser = this.authService.userProfile();
    if (currentUser && currentUser.uid === userId) {
      this.authService.userProfile.set({
        ...currentUser,
        ...updates
      });
    }
  }

  async removeAccountantEmail(userId: string): Promise<void> {
    const updates: Partial<UserProfile> = {
      accountantEmail: null,
      accountantEmailAddedAt: null
    };
    await this.updateUserProfile(userId, updates);

    // Auto-refresh: Update the local user profile state
    const currentUser = this.authService.userProfile();
    if (currentUser && currentUser.uid === userId) {
      this.authService.userProfile.set({
        ...currentUser,
        ...updates
      });
    }
  }

  private shouldFallbackToFirestoreDeletion(error: unknown): boolean {
    if (!error || typeof error !== 'object') {
      return false;
    }

    const candidate = error as {
      code?: string;
      message?: string;
      customData?: { httpStatus?: number };
    };

    const code = candidate.code?.toLowerCase() ?? '';
    const message = candidate.message?.toLowerCase() ?? '';
    const httpStatus = candidate.customData?.httpStatus;

    return code.includes('internal')
      || code.includes('unavailable')
      || message.includes('cors')
      || message.includes('failed to fetch')
      || message.includes('network request failed')
      || httpStatus === 404;
  }

  private async deleteClientAccountViaFirestore(userId: string): Promise<DeleteClientAccountResult> {
    const userRef = doc(FIRESTORE, 'users', userId);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) {
      throw new Error('Client profile not found.');
    }

    const clientProfile = userSnap.data() as UserProfile;
    if (clientProfile.role !== 'Client') {
      throw new Error('Only client accounts can be deleted.');
    }

    const clientEmail = typeof clientProfile.email === 'string' ? clientProfile.email.trim() : '';

    const [
      commitmentSnapshot,
      nestedCommitmentSnapshot,
      ticketSnapshot,
      nestedTicketSnapshot,
      notificationSnapshot,
      nestedNotificationSnapshot,
      statementSnapshot,
      relationshipAsPrimarySnapshot,
      relationshipAsAssociatedSnapshot,
      approvalAsClientSnapshot,
      approvalAsUserSnapshot,
      approvalAsTrusteeSnapshot,
      activityLogSnapshot,
      tokenSnapshot,
      individualKycSnapshot,
      corporateKycSnapshot,
      kycDraftSnapshot,
      corporateKycDraftSnapshot,
      assignedUserSnapshot,
      leadSnapshot,
    ] = await Promise.all([
      getDocs(query(collection(FIRESTORE, 'clientCommitments'), where('userId', '==', userId))),
      getDocs(collection(FIRESTORE, `users/${userId}/clientCommitments`)),
      getDocs(query(collection(FIRESTORE, 'tickets'), where('userId', '==', userId))),
      getDocs(collection(FIRESTORE, `users/${userId}/tickets`)),
      getDocs(query(collection(FIRESTORE, 'notifications'), where('userId', '==', userId))),
      getDocs(collection(FIRESTORE, `users/${userId}/notifications`)),
      getDocs(query(collection(FIRESTORE, 'redemptionStatements'), where('userId', '==', userId))),
      getDocs(query(collection(FIRESTORE, 'clientRelationships'), where('primaryClientId', '==', userId))),
      getDocs(query(collection(FIRESTORE, 'clientRelationships'), where('associatedUserId', '==', userId))),
      getDocs(query(collection(FIRESTORE, 'approvalRequests'), where('clientId', '==', userId))),
      getDocs(query(collection(FIRESTORE, 'approvalRequests'), where('userId', '==', userId))),
      getDocs(query(collection(FIRESTORE, 'approvalRequests'), where('trusteeId', '==', userId))),
      getDocs(query(collection(FIRESTORE, 'clientActivityLog'), where('clientId', '==', userId))),
      getDocs(query(collection(FIRESTORE, 'corporateKycTokens'), where('entityUserId', '==', userId))),
      getDocs(collection(FIRESTORE, `users/${userId}/kycFactFinds`)),
      getDocs(collection(FIRESTORE, `users/${userId}/corporateKycFactFinds`)),
      getDocs(collection(FIRESTORE, `users/${userId}/kycDrafts`)),
      getDocs(collection(FIRESTORE, `users/${userId}/corporateKycDrafts`)),
      getDocs(query(collection(FIRESTORE, 'users'), where('assignedClientIds', 'array-contains', userId))),
      clientEmail
        ? getDocs(query(collection(FIRESTORE, 'clientLeads'), where('contactEmail', '==', clientEmail)))
        : Promise.resolve({ docs: [], size: 0 } as Awaited<ReturnType<typeof getDocs>>),
    ]);

    const commitmentIds = commitmentSnapshot.docs.map(docSnap => docSnap.id);
    const loanLedgerDocs = await this.getLoanLedgerDocsForCommitments(commitmentIds);

    const deleted: DeleteClientAccountResult['deleted'] = {
      commitments: commitmentSnapshot.size,
      loanLedgerEntries: loanLedgerDocs.length,
      tickets: ticketSnapshot.size,
      notifications: notificationSnapshot.size,
      redemptionStatements: statementSnapshot.size,
      relationships: new Set([
        ...relationshipAsPrimarySnapshot.docs.map(docSnap => docSnap.id),
        ...relationshipAsAssociatedSnapshot.docs.map(docSnap => docSnap.id),
      ]).size,
      approvalRequests: new Set([
        ...approvalAsClientSnapshot.docs.map(docSnap => docSnap.id),
        ...approvalAsUserSnapshot.docs.map(docSnap => docSnap.id),
        ...approvalAsTrusteeSnapshot.docs.map(docSnap => docSnap.id),
      ]).size,
      activityLogs: activityLogSnapshot.size,
      corporateKycTokens: tokenSnapshot.size,
      kycSubmissions: individualKycSnapshot.size,
      corporateKycSubmissions: corporateKycSnapshot.size,
      clientLeads: leadSnapshot.size,
      storageFiles: 0,
      authDeleted: false,
    };

    for (const assignedUserDoc of assignedUserSnapshot.docs) {
      const assignedClientIds = Array.isArray(assignedUserDoc.data().assignedClientIds)
        ? assignedUserDoc.data().assignedClientIds
        : [];
      const nextAssignedIds = assignedClientIds.filter((clientId: string) => clientId !== userId);

      if (nextAssignedIds.length !== assignedClientIds.length) {
        await updateDoc(assignedUserDoc.ref, { assignedClientIds: nextAssignedIds });
      }
    }

    const docsToDelete = new Map<string, Parameters<WriteBatch['delete']>[0]>();
    [
      ...commitmentSnapshot.docs,
      ...nestedCommitmentSnapshot.docs,
      ...loanLedgerDocs,
      ...ticketSnapshot.docs,
      ...nestedTicketSnapshot.docs,
      ...notificationSnapshot.docs,
      ...nestedNotificationSnapshot.docs,
      ...statementSnapshot.docs,
      ...relationshipAsPrimarySnapshot.docs,
      ...relationshipAsAssociatedSnapshot.docs,
      ...approvalAsClientSnapshot.docs,
      ...approvalAsUserSnapshot.docs,
      ...approvalAsTrusteeSnapshot.docs,
      ...activityLogSnapshot.docs,
      ...tokenSnapshot.docs,
      ...individualKycSnapshot.docs,
      ...corporateKycSnapshot.docs,
      ...kycDraftSnapshot.docs,
      ...corporateKycDraftSnapshot.docs,
      ...leadSnapshot.docs,
    ].forEach(docSnap => {
      docsToDelete.set(docSnap.ref.path, docSnap.ref);
    });

    docsToDelete.set(userRef.path, userRef);
    await this.deleteDocumentRefsInBatches([...docsToDelete.values()]);

    return {
      success: true,
      uid: userId,
      deleted,
    };
  }

  private async getLoanLedgerDocsForCommitments(commitmentIds: string[]) {
    if (commitmentIds.length === 0) {
      return [];
    }

    const docs = new Map<string, Awaited<ReturnType<typeof getDocs>>['docs'][number]>();

    for (let index = 0; index < commitmentIds.length; index += 10) {
      const idsChunk = commitmentIds.slice(index, index + 10);
      const snapshot = await getDocs(
        query(collection(FIRESTORE, 'loanLedger'), where('sourceId', 'in', idsChunk))
      );

      snapshot.docs.forEach(docSnap => {
        docs.set(docSnap.ref.path, docSnap);
      });
    }

    return [...docs.values()];
  }

  private async deleteDocumentRefsInBatches(docRefs: Parameters<WriteBatch['delete']>[0][]): Promise<void> {
    const batchSize = 450;

    for (let index = 0; index < docRefs.length; index += batchSize) {
      const batch = writeBatch(FIRESTORE);
      const refsChunk = docRefs.slice(index, index + batchSize);

      refsChunk.forEach(docRef => batch.delete(docRef));
      await batch.commit();
    }
  }


  // Client Leads Methods
  async addLead(leadData: Omit<ClientLead, 'id'>): Promise<void> {
    await addDoc(collection(FIRESTORE, 'clientLeads'), leadData);
  }

  async updateLead(leadId: string, leadData: Record<string, unknown>): Promise<void> {
    await updateDoc(doc(FIRESTORE, 'clientLeads', leadId), leadData);
  }

  async deleteLead(leadId: string): Promise<void> {
    await deleteDoc(doc(FIRESTORE, 'clientLeads', leadId));
  }

  async confirmFundsReceived(commitment: ClientCommitment, amount: number, userId: string, dateReceived?: string): Promise<void> {
    const batch = writeBatch(FIRESTORE);
    const now = new Date().toISOString();

    // 1. Update commitment flags
    const topLevelRef = doc(FIRESTORE, 'clientCommitments', commitment.id);
    const nestedRef = doc(FIRESTORE, `users/${commitment.userId}/clientCommitments/${commitment.id}`);
    const updatePayload = {
      fundsReceived: true,
      isConfirmed: true,
      dateFundsReceived: dateReceived || now,
      dateConfirmed: commitment.dateConfirmed || now,
      confirmedByUserId: userId
    };
    batch.update(topLevelRef, updatePayload);
    batch.update(nestedRef, updatePayload);

    // 2. Add ledger entry
    const ledgerRef = doc(collection(FIRESTORE, 'loanLedger'));
    const ledgerEntry: Omit<LoanLedgerEntry, 'id'> = {
      loanDealId: commitment.loanDealId,
      date: now,
      valueDate: dateReceived || now,
      description: `Client Funds Received - ${commitment.clientName} (${commitment.accountType})`,
      amount: amount,
      entryType: LOAN_LEDGER_ENTRY_TYPE.CLIENT_FUNDS_IN,
      sourceId: commitment.id,
      accountType: commitment.accountType,
      createdBy: userId,
    };
    batch.set(ledgerRef, ledgerEntry);

    await batch.commit();
  }

  async reverseFundsReceived(commitment: ClientCommitment): Promise<void> {
    const batch = writeBatch(FIRESTORE);

    // 1. Update commitment flags
    const topLevelRef = doc(FIRESTORE, 'clientCommitments', commitment.id);
    const nestedRef = doc(FIRESTORE, `users/${commitment.userId}/clientCommitments/${commitment.id}`);
    const updatePayload = { fundsReceived: false };
    batch.update(topLevelRef, updatePayload);
    batch.update(nestedRef, updatePayload);

    // 2. Remove associated ledger entry
    const ledgerQuery = query(
      collection(FIRESTORE, 'loanLedger'),
      where('sourceId', '==', commitment.id),
      where('entryType', '==', LOAN_LEDGER_ENTRY_TYPE.CLIENT_FUNDS_IN)
    );
    const existingLedgerSnapshot = await getDocs(ledgerQuery);
    if (!existingLedgerSnapshot.empty) {
      existingLedgerSnapshot.forEach(doc => batch.delete(doc.ref));
    }

    await batch.commit();
  }

  /**
   * Efficiently check if a user manages any clients (is a Trustee or ReadOnly).
   * Uses limit(1) to avoid fetching all relationships.
   */
  async hasManagedClients(userId: string): Promise<boolean> {
    try {
      // Query for any active relationship where this user is the associated user (Trustee/ReadOnly)
      // Note: In our data model, 'associatedUserId' is the Trustee/Viewer, and 'primaryClientId' is the Client being managed.
      const relationships = await this.dataService.query<import('../models/relationship.model').ClientRelationship>(
        'clientRelationships',
        [
          { field: 'associatedUserId', operator: '==', value: userId },
          { field: 'isActive', operator: '==', value: true }
        ],
        1 // Limit to 1 for efficiency
      );
      return relationships.length > 0;
    } catch (error) {
      console.error('Error checking managed clients:', error);
      return false;
    }
  }

  /**
   * Get clients managed by a Trustee or accessible by a ReadOnly user.
   * Queries the clientRelationships collection to find active relationships.
   */
  async getManagedClients(userId: string): Promise<UserProfile[]> {
    try {
      const relationships = await this.dataService.query<import('../models/relationship.model').ClientRelationship>('clientRelationships', [
        { field: 'associatedUserId', operator: '==', value: userId },
        { field: 'isActive', operator: '==', value: true }
      ]);

      if (relationships.length === 0) {
        return [];
      }

      const clientIds = relationships.map(r => r.primaryClientId);
      const clients = await Promise.all(
        clientIds.map(async (id) => {
          const clientDoc = await getDoc(doc(FIRESTORE, 'users', id));
          return clientDoc.exists() ? { uid: id, ...clientDoc.data() } as UserProfile : null;
        })
      );

      return clients.filter((c): c is UserProfile => c !== null);
    } catch (error) {
      console.error('Error fetching managed clients:', error);
      return [];
    }
  }

  /**
   * Admin: Create a relationship between a primary client and an associated user (Trustee/ReadOnly)
   */
  async createClientRelationship(
    primaryClientId: string,
    associatedUserId: string,
    relationshipType: 'Trustee' | 'ReadOnly',
    allowedAccounts?: string[]
  ): Promise<void> {
    const admin = this.authService.userProfile();
    if (!admin || admin.role !== 'Admin') {
      throw new Error('Only admins can create client relationships');
    }

    // Fetch client and user details
    const [primaryClientDoc, associatedUserDoc] = await Promise.all([
      getDoc(doc(FIRESTORE, 'users', primaryClientId)),
      getDoc(doc(FIRESTORE, 'users', associatedUserId))
    ]);

    if (!primaryClientDoc.exists() || !associatedUserDoc.exists()) {
      throw new Error('Client or associated user not found');
    }

    const primaryClient = primaryClientDoc.data() as UserProfile;
    const associatedUser = associatedUserDoc.data() as UserProfile;

    // Set default permissions based on relationship type
    const permissions = relationshipType === 'Trustee' ? {
      canCommit: true,
      canViewDocuments: true,
      canDownloadDocuments: true,
      canViewFinancials: true
    } : {
      canCommit: false,
      canViewDocuments: true,
      canDownloadDocuments: true,
      canViewFinancials: true
    };

    const relationship: Omit<import('../models/relationship.model').ClientRelationship, 'id'> = {
      primaryClientId,
      primaryClientName: primaryClient.displayName,
      associatedUserId,
      associatedUserName: associatedUser.displayName,
      associatedUserEmail: associatedUser.email,
      relationshipType,
      ...permissions,
      ...(allowedAccounts && { allowedAccounts }),
      createdAt: new Date().toISOString(),
      createdBy: admin.uid,
      isActive: true
    };

    // Use setDoc with a composite ID so security rules can find it easily
    const relationshipId = `${primaryClientId}_${associatedUserId}`;
    await setDoc(doc(FIRESTORE, 'clientRelationships', relationshipId), {
      ...relationship,
      id: relationshipId
    });

    // Update the associated user's subRole if not already set, and add the client ID to assignedClientIds
    const userUpdates: Partial<UserProfile> = {};

    if (!associatedUser.subRole || associatedUser.subRole === 'Standard') {
      userUpdates.subRole = relationshipType;
    }

    // Add to assignedClientIds if not already present
    const currentAssignedIds = associatedUser.assignedClientIds || [];
    if (!currentAssignedIds.includes(primaryClientId)) {
      userUpdates.assignedClientIds = [...currentAssignedIds, primaryClientId];
    }

    if (Object.keys(userUpdates).length > 0) {
      await updateDoc(doc(FIRESTORE, 'users', associatedUserId), userUpdates);
    }
  }

  /**
   * Admin: Deactivate a client relationship
   */
  async deactivateClientRelationship(relationshipId: string): Promise<void> {
    const admin = this.authService.userProfile();
    if (!admin || admin.role !== 'Admin') {
      throw new Error('Only admins can deactivate relationships');
    }

    await updateDoc(doc(FIRESTORE, 'clientRelationships', relationshipId), {
      isActive: false
    });
  }

  /**
   * Admin: Reactivate a client relationship
   */
  async reactivateClientRelationship(relationshipId: string): Promise<void> {
    const admin = this.authService.userProfile();
    if (!admin || admin.role !== 'Admin') {
      throw new Error('Only admins can reactivate relationships');
    }

    await updateDoc(doc(FIRESTORE, 'clientRelationships', relationshipId), {
      isActive: true
    });
  }

  /**
   * Admin: Completely delete a client relationship
   */
  async deleteClientRelationship(relationshipId: string): Promise<void> {
    const admin = this.authService.userProfile();
    if (!admin || admin.role !== 'Admin') {
      throw new Error('Only admins can delete relationships');
    }

    // First, get the relationship to extract the user ID and client ID
    const relationshipDoc = await getDoc(doc(FIRESTORE, 'clientRelationships', relationshipId));
    if (!relationshipDoc.exists()) {
      throw new Error('Relationship not found');
    }

    const relationship = relationshipDoc.data() as import('../models/relationship.model').ClientRelationship;

    // Remove the client ID from the user's assignedClientIds
    const userDoc = await getDoc(doc(FIRESTORE, 'users', relationship.associatedUserId));
    if (userDoc.exists()) {
      const userData = userDoc.data() as UserProfile;
      const currentAssignedIds = userData.assignedClientIds || [];
      const updatedAssignedIds = currentAssignedIds.filter(id => id !== relationship.primaryClientId);

      await updateDoc(doc(FIRESTORE, 'users', relationship.associatedUserId), {
        assignedClientIds: updatedAssignedIds
      });
    }

    // Delete the relationship document
    await deleteDoc(doc(FIRESTORE, 'clientRelationships', relationshipId));
  }

  /**
   * Admin: Get all relationships for a specific client
   */
  async getRelationshipsForClient(clientId: string): Promise<import('../models/relationship.model').ClientRelationship[]> {
    try {
      return await this.dataService.query<import('../models/relationship.model').ClientRelationship>('clientRelationships', [
        { field: 'primaryClientId', operator: '==', value: clientId }
      ]);
    } catch (error) {
      console.error('Error fetching relationships for client:', error);
      return [];
    }
  }

  /**
   * User: Get all clients this user can access with relationship details
   */
  async getAccessibleClients(userId: string): Promise<import('../models/relationship.model').AccessibleClient[]> {
    console.log('[TRUSTEE DEBUG] ========================================');
    console.log('[TRUSTEE DEBUG] getAccessibleClients() called');
    console.log('[TRUSTEE DEBUG] userId:', userId);
    console.log('[TRUSTEE DEBUG] ========================================');

    try {
      console.log('[TRUSTEE DEBUG] Step 1: Querying clientRelationships...');
      console.log('[TRUSTEE DEBUG] Query filters:', {
        primaryClientId: userId,
        isActive: true
      });

      // FIXED: Query by primaryClientId (trustee) instead of associatedUserId to match UI convention
      const relationships = await this.dataService.query<import('../models/relationship.model').ClientRelationship>('clientRelationships', [
        { field: 'primaryClientId', operator: '==', value: userId },
        { field: 'isActive', operator: '==', value: true }
      ]);

      console.log('[TRUSTEE DEBUG] Step 2: Query completed');
      console.log('[TRUSTEE DEBUG] Found', relationships.length, 'relationship(s)');

      if (relationships.length === 0) {
        console.log('[TRUSTEE DEBUG] No relationships found - returning empty array');
        return [];
      }

      console.log('[TRUSTEE DEBUG] Relationships found:', relationships.map(r => ({
        id: r.id,
        clientId: r.associatedUserId,
        clientName: r.associatedUserName,
        relationshipType: r.relationshipType,
        isActive: r.isActive
      })));

      const result: import('../models/relationship.model').AccessibleClient[] = [];

      console.log('[TRUSTEE DEBUG] Step 3: Processing relationships...');
      for (const relationship of relationships) {
        // In our convention: associatedUserId is the client being managed
        console.log('[TRUSTEE DEBUG] Processing relationship for client:', relationship.associatedUserId);

        try {
          // In our convention: associatedUserId is the client being managed
          const clientDoc = await getDoc(doc(FIRESTORE, 'users', relationship.associatedUserId));

          if (!clientDoc.exists()) {
            console.warn('[TRUSTEE DEBUG] Client document not found for ID:', relationship.associatedUserId);
            continue;
          }

          const client = { uid: relationship.associatedUserId, ...clientDoc.data() } as UserProfile;
          console.log('[TRUSTEE DEBUG] Client found:', client.displayName);

          // Get stats for this client
          console.log('[TRUSTEE DEBUG] Fetching commitments for client...');
          const clientCommitments = this.allCommitments().filter(c => c.userId === client.uid);
          console.log('[TRUSTEE DEBUG] Found', clientCommitments.length, 'commitments');

          const totalCommitted = clientCommitments.reduce((sum, c) => sum + c.committedAmount, 0);
          const deals = this.dataService.allDeals();
          console.log('[TRUSTEE DEBUG] Retrieved all deals:', deals.length);
          const dealIds = new Set(clientCommitments.map(c => c.loanDealId));
          console.log('[TRUSTEE DEBUG] Unique deal IDs from commitments:', dealIds.size);

          let activeDealsCount = 0;
          let fundedDealsCount = 0;

          for (const dealId of dealIds) {
            const deal = deals.find(d => d.loanDeal.id === dealId);
            if (deal) {
              console.log(`[TRUSTEE DEBUG] Found deal ${dealId} with status: ${deal.loanDeal.status}`);
              if (deal.loanDeal.status === LOAN_STATUS.ACTIVE) activeDealsCount++;
              if (deal.loanDeal.status === LOAN_STATUS.FUNDED || deal.loanDeal.status === LOAN_STATUS.EXTENDED) fundedDealsCount++;
            } else {
              console.warn(`[TRUSTEE DEBUG] Deal ${dealId} not found in allDeals()`);
            }
          }

          console.log('[TRUSTEE DEBUG] Client stats:', {
            totalCommitted,
            activeDealsCount,
            fundedDealsCount
          });

          result.push({
            client,
            relationship,
            stats: {
              totalCommitted,
              activeDealsCount,
              fundedDealsCount
            }
          });

          console.log('[TRUSTEE DEBUG] Added client to result array');
        } catch (clientError) {
          console.error('[TRUSTEE DEBUG] Error processing client:', relationship.associatedUserId, clientError);
        }
      }

      console.log('[TRUSTEE DEBUG] ========================================');
      console.log('[TRUSTEE DEBUG] Final result: Returning', result.length, 'accessible client(s)');
      console.log('[TRUSTEE DEBUG] ========================================');
      return result;
    } catch (error) {
      console.error('[TRUSTEE DEBUG] ========================================');
      console.error('[TRUSTEE DEBUG] ERROR in getAccessibleClients');
      console.error('[TRUSTEE DEBUG] Error:', error);
      console.error('[TRUSTEE DEBUG] Error message:', error instanceof Error ? error.message : 'Unknown');
      console.error('[TRUSTEE DEBUG] Error code:', (error as any)?.code);
      console.error('[TRUSTEE DEBUG] Error stack:', error instanceof Error ? error.stack : 'N/A');
      console.error('[TRUSTEE DEBUG] ========================================');
      return [];
    }
  }

  /**
   * Get a user profile by ID
   */
  async getUserProfile(userId: string): Promise<UserProfile | null> {
    try {
      const userDoc = await getDoc(doc(FIRESTORE, 'users', userId));
      return userDoc.exists() ? { uid: userId, ...userDoc.data() } as UserProfile : null;
    } catch (error) {
      console.error('Error fetching user profile:', error);
      return null;
    }
  }
}
